import { useEffect, useState } from "react";

import { getValidAccessToken, onAuthFailed } from "./accessToken";

const parent = new URL(process.env.REACT_APP_PARENT_ORIGIN || "https://topteacher.com.au");
parent.pathname = "/my-account";
const parentOrigin = parent.toString();

type AuthStatus = "loading" | "authenticated" | "failed";

// The dashboard usually runs inside the web app's iframe; navigate the
// top-level window so the user lands on /my-account itself, not the dashboard
// nested inside it. Falls back to same-window navigation when standalone.
const openMyAccount = (): void => {
  if (window.top && window.top !== window.self) {
    window.top.location.href = parentOrigin;
    return;
  }
  window.location.href = parentOrigin;
};

// Gates rendering behind a valid token obtained from the parent window.
export const Auth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>("loading");

  // Acquire a valid token before rendering the app, and keep listening: the
  // token can expire later, when a save/load asks for a fresh one and the
  // parent cannot supply it
  useEffect(() => {
    let cancelled = false;

    getValidAccessToken().then((token) => {
      if (cancelled) return;
      setStatus(token ? "authenticated" : "failed");
    });

    const unsubscribe = onAuthFailed(() => {
      if (!cancelled) setStatus("failed");
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (status === "loading") {
    return (
      <div
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "16px",
          color: "#666",
        }}
      >
        <div>Loading dashboard...</div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div
        style={{
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "12px",
            background: "#FDECEA",
            border: "1px solid #F2B8B5",
            borderRadius: "20px",
            padding: "32px 24px",
            maxWidth: "440px",
          }}
        >
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#B3261E" }}>
            We couldn&apos;t load your dashboard
          </div>
          <div style={{ color: "#69727d", lineHeight: 1.5 }}>
            Your session may have expired or something went wrong on our end. Log out and sign in
            again from your TopTeacher account.
          </div>
          <button
            type="button"
            onClick={openMyAccount}
            style={{
              marginTop: "16px",
              minWidth: "180px",
              padding: "12px 32px",
              fontSize: "1rem",
              fontWeight: 700,
              color: "#fff",
              backgroundColor: "#D32F2F",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Back to My Account
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
